import React, { useState } from "react";
import { useOutletContext } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Send, Instagram } from "lucide-react";

export default function Contatti() {
  const { content } = useOutletContext() || {};
  const c = content || {};
  const [form, setForm] = useState({ name:"", email:"", phone:"", subject:"", message:"" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || form.name.length < 2) return toast.error("Inserisci il tuo nome");
    if (!form.email) return toast.error("Inserisci una email valida");
    if (!form.subject || form.subject.length < 2) return toast.error("Aggiungi un oggetto");
    if (!form.message || form.message.length < 10) return toast.error("Scrivi un messaggio di almeno 10 caratteri");
    setBusy(true);
    try {
      await api.post("/contact", form);
      toast.success("Messaggio inviato! Ti risponderemo presto.");
      setSent(true);
      setForm({ name:"", email:"", phone:"", subject:"", message:"" });
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 429) toast.error("Troppe richieste. Riprova tra un'ora.");
      else toast.error(typeof detail === "string" ? detail : "Errore nell'invio. Riprova.");
    }
    setBusy(false);
  };

  return (
    <section className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-16">
      <div className="mb-10">
        <div className="tag-pill mb-3"><Mail size={12}/> Contattaci</div>
        <h1 className="font-display text-5xl sm:text-6xl uppercase text-white" data-testid="contatti-heading">
          {c.contatti_title || "Scrivici"}
        </h1>
        <p className="mt-3 text-white/70 max-w-2xl whitespace-pre-line">
          {c.contatti_subtitle || "Domande, collaborazioni, proposte per eventi o podcast? Compila il modulo o scrivici direttamente."}
        </p>
      </div>

      <div className="grid lg:grid-cols-5 gap-8">
        {/* Info column */}
        <div className="lg:col-span-2 space-y-5">
          <div className="card p-5">
            <div className="flex items-start gap-3">
              <Mail size={18} className="mt-1 text-white shrink-0"/>
              <div>
                <div className="text-xs font-mono uppercase tracking-widest text-white/70">Email</div>
                {c.contact_email ? (
                  <a href={`mailto:${c.contact_email}`} className="mt-1 block text-white hover:underline break-all" data-testid="info-email">
                    {c.contact_email}
                  </a>
                ) : <div className="mt-1 text-white/50">non impostata</div>}
              </div>
            </div>
          </div>

          <div className="card p-5">
            <div className="flex items-start gap-3">
              <MapPin size={18} className="mt-1 text-white shrink-0"/>
              <div>
                <div className="text-xs font-mono uppercase tracking-widest text-white/70">Sede</div>
                <div className="mt-1 text-white whitespace-pre-line">{c.footer_text || "Flow Bologna · Via Cesare Boldrini 12"}</div>
                <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(c.footer_text || "Flow Bologna Via Cesare Boldrini 12 Bologna")}`}
                  target="_blank" rel="noopener noreferrer"
                  className="mt-2 inline-block text-xs text-white/70 hover:text-white underline" data-testid="info-map">
                  Apri in Google Maps
                </a>
              </div>
            </div>
          </div>

          {c.social_instagram && (
            <div className="card p-5">
              <div className="flex items-start gap-3">
                <Instagram size={18} className="mt-1 text-white shrink-0"/>
                <div>
                  <div className="text-xs font-mono uppercase tracking-widest text-white/70">Instagram</div>
                  <a href={c.social_instagram} target="_blank" rel="noopener noreferrer"
                    className="mt-1 block text-white hover:underline break-all">
                    {c.social_instagram.replace(/^https?:\/\//, "")}
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Form column */}
        <div className="lg:col-span-3">
          {sent ? (
            <div className="card p-10 text-center">
              <div className="w-14 h-14 border border-white flex items-center justify-center mx-auto">
                <Send size={22}/>
              </div>
              <h3 className="mt-5 font-display text-3xl uppercase text-white">Messaggio inviato</h3>
              <p className="mt-2 text-white/70">Grazie! Ti risponderemo appena possibile.</p>
              <button onClick={() => setSent(false)} className="brutal-btn brutal-btn-outline mt-6 text-xs" data-testid="send-another">
                Invia un altro messaggio
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="card p-6 grid gap-4" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Nome e cognome" tid="cf-name" val={form.name} onChange={v => setForm({...form, name: v})} required/>
                <Field label="Email" tid="cf-email" type="email" val={form.email} onChange={v => setForm({...form, email: v})} required/>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Telefono (opzionale)" tid="cf-phone" val={form.phone} onChange={v => setForm({...form, phone: v})}/>
                <Field label="Oggetto" tid="cf-subject" val={form.subject} onChange={v => setForm({...form, subject: v})} required/>
              </div>
              <div>
                <label className="block text-xs font-mono uppercase tracking-widest text-white mb-1">Messaggio *</label>
                <textarea rows={7} value={form.message} onChange={e => setForm({...form, message: e.target.value})}
                  placeholder="Scrivici qui..." required data-testid="cf-message"
                  className="w-full bg-black border border-white/40 focus:border-white px-3 py-3 rounded text-white"/>
              </div>
              <button type="submit" disabled={busy} className="brutal-btn brutal-btn-primary" data-testid="cf-submit">
                <Send size={16}/> {busy ? "Invio in corso..." : "Invia messaggio"}
              </button>
              <p className="text-xs text-white/50 text-center">
                Inviando il modulo accetti che i tuoi dati vengano usati per rispondere alla richiesta.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

function Field({ label, tid, val, onChange, type="text", required=false }) {
  return (
    <div>
      <label className="block text-xs font-mono uppercase tracking-widest text-white mb-1">
        {label}{required && " *"}
      </label>
      <input data-testid={tid} type={type} value={val} onChange={e => onChange(e.target.value)} required={required}
        className="w-full bg-black border border-white/40 focus:border-white px-3 py-3 rounded text-white"/>
    </div>
  );
}
